/**
 * @fileoverview Endless Runner Index Screen
 * @description Lobby screen for difficulty selection and mode choice
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { router, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { GameModeSelector } from "@/modules/games";
import {
  RunnerDifficulty,
  RUNNER_DIFFICULTY_CONFIGS,
  RunnerUserStats,
  EMPTY_RUNNER_STATS,
  RunnerWeekendConfig,
  DEFAULT_RUNNER_WEEKEND_CONFIG,
  CharacterId,
  CharacterAbilitySettings,
  DEFAULT_CHARACTER_ABILITY_SETTINGS,
} from "@/modules/endless-runner";
import { RunnerDifficultyCard } from "@/modules/endless-runner/components/RunnerDifficultyCard";
import { RunnerLeaderboard } from "@/modules/endless-runner/components/RunnerLeaderboard";
import { CharacterPicker } from "@/modules/endless-runner/components/CharacterPicker";

type SessionMode = "free" | "weekend" | "practice";

/**
 * EndlessRunnerIndexScreen - Lobby and difficulty selection
 */
export default function EndlessRunnerIndexScreen() {
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { activeProfile, activeWeekend, userTeam } = useAuth();
  const { mode: urlMode } = useLocalSearchParams<{ mode?: string }>();

  const targetUserId = activeProfile?.id;

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedMode, setSelectedMode] = useState<SessionMode>(
    urlMode === "weekend" ? "weekend" : "free"
  );
  const [gameEnabledForWeekend, setGameEnabledForWeekend] = useState(false);
  const [weekendConfig, setWeekendConfig] = useState<RunnerWeekendConfig>(
    DEFAULT_RUNNER_WEEKEND_CONFIG
  );
  const [stats, setStats] = useState<RunnerUserStats>(EMPTY_RUNNER_STATS);
  const [weekendStats, setWeekendStats] =
    useState<RunnerUserStats>(EMPTY_RUNNER_STATS);
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterId>("opa");
  const [abilitySettings, setAbilitySettings] = useState<CharacterAbilitySettings>(
    DEFAULT_CHARACTER_ABILITY_SETTINGS
  );

  // Determine if weekend mode is available
  const isWeekendModeAvailable =
    !!activeWeekend && !!userTeam && gameEnabledForWeekend;
  const weekendUnavailableReason = !activeWeekend
    ? "Geen actief weekend"
    : !userTeam
    ? "Je zit nog niet in een team"
    : !gameEnabledForWeekend
    ? "Niet geactiveerd voor dit weekend"
    : "";

  /**
   * Fetch weekend game config
   */
  const fetchWeekendGameConfig = useCallback(async () => {
    if (!activeWeekend) {
      setGameEnabledForWeekend(false);
      return;
    }

    try {
      const { data } = await supabase
        .from("weekend_game_config")
        .select("is_enabled, points_config, custom_config")
        .eq("weekend_id", activeWeekend.id)
        .eq("game_slug", "endless_runner")
        .single();

      setGameEnabledForWeekend(data?.is_enabled ?? false);

      if (data?.custom_config) {
        setWeekendConfig({
          ...DEFAULT_RUNNER_WEEKEND_CONFIG,
          ...(data.custom_config as Partial<RunnerWeekendConfig>),
        });
      }

      if (data?.points_config) {
        const pointsConfig = data.points_config as Record<string, number>;
        setWeekendConfig((prev) => ({
          ...prev,
          points_new_highscore:
            pointsConfig.points_new_highscore ?? prev.points_new_highscore,
          points_participation:
            pointsConfig.points_participation ?? prev.points_participation,
        }));
      }
    } catch {
      setGameEnabledForWeekend(false);
    }
  }, [activeWeekend]);

  /**
   * Fetch user stats
   */
  const fetchStats = useCallback(async () => {
    if (!targetUserId) return;

    try {
      // Fetch free play stats
      const { data: freeData } = await supabase
        .from("user_game_stats")
        .select("stats")
        .eq("user_id", targetUserId)
        .eq("game_slug", "endless_runner")
        .eq("mode", "free")
        .single();

      if (freeData?.stats) {
        setStats(freeData.stats as RunnerUserStats);
      }

      // Fetch weekend stats if active weekend
      if (activeWeekend) {
        const { data: weekendData } = await supabase
          .from("user_game_stats")
          .select("stats")
          .eq("user_id", targetUserId)
          .eq("game_slug", "endless_runner")
          .eq("mode", "weekend")
          .single();

        if (weekendData?.stats) {
          setWeekendStats(weekendData.stats as RunnerUserStats);
        }
      }
    } catch {
      // No stats yet
    }
  }, [targetUserId, activeWeekend]);

  /**
   * Fetch ability settings from game definition
   */
  const fetchAbilitySettings = useCallback(async () => {
    try {
      const { data: gameDef } = await supabase
        .from("game_definitions")
        .select("default_config")
        .eq("slug", "endless_runner")
        .single();

      if (gameDef?.default_config) {
        const config = gameDef.default_config as Record<string, any>;
        if (config.ability_settings) {
          setAbilitySettings({
            ...DEFAULT_CHARACTER_ABILITY_SETTINGS,
            ...config.ability_settings,
          });
        }
      }
    } catch {
      // Use defaults
    }
  }, []);

  /**
   * Initial data fetch
   */
  useEffect(() => {
    const loadData = async () => {
      setIsFetchingData(true);
      await Promise.all([fetchWeekendGameConfig(), fetchStats(), fetchAbilitySettings()]);
      setIsFetchingData(false);
    };

    loadData();
  }, [fetchWeekendGameConfig, fetchStats, fetchAbilitySettings]);

  /**
   * Handle pull-to-refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([fetchWeekendGameConfig(), fetchStats(), fetchAbilitySettings()]);
    setIsRefreshing(false);
  }, [fetchWeekendGameConfig, fetchStats, fetchAbilitySettings]);

  /**
   * Start game with selected difficulty
   */
  const handleStartGame = async (difficulty: RunnerDifficulty) => {
    if (!targetUserId) return;

    setIsLoading(true);

    try {
      // Create initial game state with character
      const initialGameState = {
        difficulty,
        characterId: selectedCharacter,
        score: 0,
        highScore: 0,
        isPlaying: true,
        isPaused: false,
        isGameOver: false,
        jumps: 0,
        obstaclesAvoided: 0,
        startedAt: new Date().toISOString(),
        endedAt: null,
        timePlayedSeconds: 0,
        livesRemaining: selectedCharacter === "oma" ? abilitySettings.oma.lives : 1,
        specialJumpsUsed: 0,
      };

      // Create game session
      const { data: session, error: sessionError } = await supabase
        .from("game_sessions")
        .insert({
          game_slug: "endless_runner",
          created_by: targetUserId,
          participants: [
            {
              user_id: targetUserId,
              role: "player",
              name: activeProfile?.full_name ?? "Speler",
              team_id: userTeam?.id,
            },
          ],
          mode: selectedMode,
          session_type: "solo",
          status: "active",
          game_state: initialGameState,
          weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
        })
        .select()
        .single();

      if (sessionError || !session) {
        throw new Error("Kon sessie niet aanmaken");
      }

      router.push(
        `/(tabs)/games/endless-runner/${session.id}?difficulty=${difficulty}&character=${selectedCharacter}` as any
      );
    } catch (err) {
      console.error("[EndlessRunner] Error starting game:", err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get stats for current mode and difficulty
   */
  const getStatsForDifficulty = (difficulty: RunnerDifficulty) => {
    const currentStats = selectedMode === "weekend" ? weekendStats : stats;
    return currentStats[difficulty];
  };

  /**
   * Get total stats for display
   */
  const getTotalStats = () => {
    const currentStats = selectedMode === "weekend" ? weekendStats : stats;
    return {
      totalGames: currentStats.total_games,
      totalScore: currentStats.total_score,
      bestScore: Math.max(
        currentStats.easy.best_score,
        currentStats.medium.best_score,
        currentStats.hard.best_score
      ),
    };
  };

  const totalStats = getTotalStats();

  if (isFetchingData) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: colors.background }]}
        edges={["top", "left", "right"]}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
            Laden...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={["top", "left", "right"]}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.back()}
          style={styles.backButton}
          accessibilityLabel="Terug"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <Text style={[styles.title, { color: colors.text }]}>
            🏃 Endless Runner
          </Text>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            Ren, spring en ontwijken!
          </Text>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Game Mode */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Spelmodus
          </Text>
          <GameModeSelector
            mode={selectedMode}
            onModeChange={setSelectedMode}
            weekendAvailable={isWeekendModeAvailable}
            weekendUnavailableReason={weekendUnavailableReason}
            isDark={isDark}
            compact
          />
        </View>

        {/* Character Selection */}
        <View style={styles.section}>
          <CharacterPicker
            selectedCharacter={selectedCharacter}
            onSelect={setSelectedCharacter}
            abilitySettings={abilitySettings}
            disabled={isLoading}
          />
        </View>

        {/* Stats Summary */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            {selectedMode === "weekend"
              ? "Weekend Statistieken"
              : "Jouw Statistieken"}
          </Text>
          <View
            style={[styles.statsCard, { backgroundColor: colors.surface }]}
          >
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: colors.text }]}>
                {totalStats.totalGames}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Gespeeld
              </Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: "#F59E0B" }]}>
                {totalStats.bestScore.toLocaleString()}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Hoogste Score
              </Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <Text style={[styles.statValue, { color: "#22C55E" }]}>
                {totalStats.totalScore.toLocaleString()}
              </Text>
              <Text
                style={[styles.statLabel, { color: colors.textSecondary }]}
              >
                Totaal
              </Text>
            </View>
          </View>
        </View>

        {/* Difficulty Selection */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Kies moeilijkheid
          </Text>

          {(["easy", "medium", "hard"] as RunnerDifficulty[]).map(
            (difficulty) => {
              const config = RUNNER_DIFFICULTY_CONFIGS[difficulty];
              const diffStats = getStatsForDifficulty(difficulty);
              // Show participation points (min) to max possible (participation + new highscore)
              const minPoints = selectedMode === "weekend" ? weekendConfig.points_participation : undefined;
              const maxPoints = selectedMode === "weekend" 
                ? weekendConfig.points_participation + weekendConfig.points_new_highscore 
                : undefined;

              return (
                <RunnerDifficultyCard
                  key={difficulty}
                  config={config}
                  bestScore={diffStats.best_score}
                  gamesPlayed={diffStats.games_played}
                  points={minPoints}
                  maxPoints={maxPoints}
                  isWeekendMode={selectedMode === "weekend"}
                  onPress={handleStartGame}
                  disabled={isLoading}
                />
              );
            }
          )}
        </View>

        {/* Leaderboard */}
        <View style={styles.section}>
          <RunnerLeaderboard mode={selectedMode === "weekend" ? "weekend" : "free"} maxEntries={5} />
        </View>

        {/* Weekend Mode Info */}
        {selectedMode === "weekend" && isWeekendModeAvailable && (
          <View style={styles.section}>
            <View
              style={[styles.infoCard, { backgroundColor: colors.surface }]}
            >
              <View style={styles.infoHeader}>
                <Ionicons name="trophy" size={20} color="#F59E0B" />
                <Text style={[styles.infoTitle, { color: colors.text }]}>
                  Weekend Modus
                </Text>
              </View>
              <Text
                style={[styles.infoText, { color: colors.textSecondary }]}
              >
                Verdien punten voor team {userTeam?.name}! Versla het familie
                record voor extra punten.
              </Text>
              <View style={styles.pointsInfo}>
                <Ionicons name="trophy" size={14} color="#22C55E" />
                <Text style={[styles.pointsText, { color: "#22C55E" }]}>
                  +{weekendConfig.points_new_highscore} punten voor nieuw familie record
                </Text>
              </View>
              <View style={styles.pointsInfo}>
                <Ionicons name="game-controller" size={14} color="#F59E0B" />
                <Text style={[styles.pointsText, { color: "#F59E0B" }]}>
                  +{weekendConfig.points_participation} punten per spel (deelname)
                </Text>
              </View>
            </View>
          </View>
        )}
      </ScrollView>

      {/* Loading Overlay */}
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#FFFFFF" />
          <Text style={styles.loadingOverlayText}>Spel starten...</Text>
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
  },
  loadingText: {
    fontSize: 16,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255, 255, 255, 0.1)",
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: 14,
    marginTop: 2,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    gap: 20,
    paddingBottom: 40,
  },
  section: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  statsCard: {
    flexDirection: "row",
    borderRadius: 16,
    padding: 16,
    justifyContent: "space-around",
  },
  statItem: {
    alignItems: "center",
    flex: 1,
  },
  statValue: {
    fontSize: 20,
    fontWeight: "bold",
  },
  statLabel: {
    fontSize: 11,
    marginTop: 4,
  },
  statDivider: {
    width: 1,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  infoCard: {
    padding: 16,
    borderRadius: 16,
    gap: 8,
  },
  infoHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  infoText: {
    fontSize: 14,
    lineHeight: 20,
  },
  pointsInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 4,
  },
  pointsText: {
    fontSize: 13,
    fontWeight: "500",
  },
  milestonesRow: {
    marginTop: 4,
  },
  milestonesTitle: {
    fontSize: 12,
    fontWeight: "500",
  },
  milestonesText: {
    fontSize: 11,
    marginTop: 2,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
  },
  loadingOverlayText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "500",
  },
});

